/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import './ExternalAuthorizationForm.scss';

import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';

import If from 'components/shared/If/If';

import { isBrokerageOwner } from 'state/view/ViewSelectors';

import { getBrokerageAccountDescriptors } from 'state/account/AccountSelectors';

import ErrorPage from 'components/shared/ErrorPage/ErrorPage';

import { Spinner } from '@trp-beacon-ui-kit/react-communication';

import { postExternalAuthorization } from 'services/Rest';

import {
  brokerageAuthTokenUrls,
  educationAuthTokenUrls,
  wallStreetJournalAuthTokenUrls
} from 'services/Externals';

import {
  REQUEST_STATE_REQUESTED,
  REQUEST_STATE_RECEIVED,
  REQUEST_STATE_ERROR
} from 'state/requests/RequestStatusReducer';

import { VENDOR_MAP } from 'services/Vendor';

import { getSearchParams } from 'services/globals';
import { brokerageFrameEnabled } from "services/FeatureFlags";
import { TRADING_IFRAME_TARGETS } from "services/ExternalAuthorizationURLBuilder";

const EducationSSOErrorPage = () => (
  <ErrorPage>
    <div className="sso-form-error-message">
      The Invite Education page is currently unavailable. We apologize for the
      inconvenience. For further assistance, please{' '}
      <a className="error-page-link" href={educationAuthTokenUrls.contactUrl}>
        contact
      </a>{' '}
      a T. Rowe Price representative.
    </div>
  </ErrorPage>
);

const SSORequestSpinner = () => (
  <div className="sso-form-overlay">
    <p className="sso-form-loading-message"> Authorizing Account </p>
    <Spinner />
  </div>
);

class ExternalAuthorizationForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      reqStatus: null,
      token: null,
      containerApplicationUrl: '',
      params: {},
      requestBody: {}
    };

    this.formRef = createRef();
  }

  componentDidMount() {
    const {
      location: { search },
      isBrokerage
    } = this.props;

    const params = getSearchParams(search);
    this.setState({ params });

    const vendor = params.vendor ? params.vendor : '';
    const selectedAccountId = params.selectedAccountId
      ? params.selectedAccountId
      : '';

    const VENDOR_ID = VENDOR_MAP[vendor];

    let requestBody = {};

    if (VENDOR_ID > 0) {
      requestBody = {
        serviceTypeId: `${VENDOR_ID}`
      };
    }

    if (selectedAccountId && selectedAccountId !== 'undefined') {
      requestBody = {
        ...requestBody,
        selectedAccountId
      };
    }

    if (isBrokerage || VENDOR_ID === 700) {
      const { errorUrl, keepAliveUrl } = brokerageAuthTokenUrls;

      requestBody = {
        ...requestBody,
        errorUrl,
        keepAliveUrl
      };
    }

    if (VENDOR_ID === 1100 || VENDOR_ID === 1200) {
      requestBody = {
        ...requestBody,
        errorUrl: educationAuthTokenUrls.errorUrl
      };
    }

    if (VENDOR_ID === 1300) {
      requestBody = {
        ...requestBody,
        errorUrl: wallStreetJournalAuthTokenUrls.errorUrl
      };
    }
    this.setState({ requestBody });
  }

  componentDidUpdate(prevProps, prevState) {
    const { accountDescriptors, requestConfig } = this.props;
    const { reqStatus: pastRequestStatus } = prevState;
    const { reqStatus: currentRequestStatus, requestBody } = this.state;
    const requestHasUpdated = pastRequestStatus !== currentRequestStatus;
    const accountsPreviouslyRequested =
      prevProps.requestConfig.accounts === REQUEST_STATE_REQUESTED;
    const isSuccessfulRequest = currentRequestStatus === REQUEST_STATE_RECEIVED;
    const isErrorRequest = currentRequestStatus === REQUEST_STATE_ERROR;
    const accountsReceived = requestConfig.accounts === REQUEST_STATE_RECEIVED;
    const accountsError = requestConfig.accounts === REQUEST_STATE_ERROR;

    if (!isErrorRequest) {
      if (accountsReceived && accountsPreviouslyRequested) {
        let body = {
          ...requestBody
        };
        if (accountDescriptors && accountDescriptors.length > 0) {
          body = {
            ...body,
            accountDescriptors: accountDescriptors
          };
        }
        this.setState({ reqStatus: REQUEST_STATE_REQUESTED });

        postExternalAuthorization(body).then(
          // When using `setState` it is better to use `.then( resolve, reject )` rather than `.then( resolve ).catch( reject )` because the later will swallow any errors that come from `setState`
          ({ assertionText: token, containerApplicationUrl }) =>
            this.setState({
              reqStatus: REQUEST_STATE_RECEIVED,
              token,
              containerApplicationUrl
            }),
          () => this.setState({ reqStatus: REQUEST_STATE_ERROR })
        );
      }
      if (accountsError && accountsPreviouslyRequested) {
        this.setState({ reqStatus: REQUEST_STATE_REQUESTED });
        postExternalAuthorization(requestBody).then(
          // When using `setState` it is better to use `.then( resolve, reject )` rather than `.then( resolve ).catch( reject )` because the later will swallow any errors that come from `setState`
          ({ assertionText: token, containerApplicationUrl }) =>
            this.setState({
              reqStatus: REQUEST_STATE_RECEIVED,
              token,
              containerApplicationUrl
            }),
          () => this.setState({ reqStatus: REQUEST_STATE_ERROR })
        );
      }
    }

    if (requestHasUpdated && isSuccessfulRequest) {
      this.formRef.current.submit();
    }
  }

  customErrorCheck = (vendorId, codes) => {
    const CUSTOM_ERROR_PAGE_LIST = [700, 701, 1100, 1200, 1300];
    const isCustomErrorPage = codes.some(
      (code) => CUSTOM_ERROR_PAGE_LIST.indexOf(code) !== -1
    );
    const isVendorIncludedInCheck = codes.some((code) => code === vendorId);
    return isCustomErrorPage && isVendorIncludedInCheck;
  };

  render() {
    const {
      params: { target, vendor },
      reqStatus,
      token,
      containerApplicationUrl
    } = this.state;

    const {
      requestConfig,
      ssoIndicator = 'NXI_SSOIN_IDPINIT_TROWE',
      location
    } = this.props;

    const accountsRequested = requestConfig.accounts === 'REQUESTED';
    const VENDOR_ID = VENDOR_MAP[vendor];

    return (
      <div>
        { brokerageFrameEnabled() && TRADING_IFRAME_TARGETS.some(it => it === target) &&
          <Redirect to={`/brokerage?action=trade&target=${target}`} />
        }
        <If test={reqStatus === REQUEST_STATE_REQUESTED || accountsRequested}>
          <SSORequestSpinner />
        </If>
        <If test={reqStatus === REQUEST_STATE_ERROR}>
          <If test={this.customErrorCheck(VENDOR_ID, [700, 701])}>
            <Redirect
              to={{
                pathname: 'brokerage-unavailable',
                state: { from: location }
              }}
            />
          </If>
          <If test={this.customErrorCheck(VENDOR_ID, [1100, 1200, 1201])}>
            <EducationSSOErrorPage />
          </If>
          <If test={this.customErrorCheck(VENDOR_ID, [1300])}>
            <Redirect to="wsj-unavailable" />
          </If>
          <If test={!this.customErrorCheck(VENDOR_ID, [VENDOR_ID])}>
            <ErrorPage />
          </If>
        </If>
        <div className="sso-form-container">
          <div className="sso-form-overlay-blur" />
          <form
            ref={this.formRef}
            name="sso_form"
            method="POST"
            action={containerApplicationUrl}>
            {token && <input type="hidden" name="SAMLResponse" value={token} />}
            {ssoIndicator && (
              <input type="hidden" name="SSOIndicator" value={ssoIndicator} />
            )}
            {target && <input type="hidden" name="Target" value={target} />}
            <input type="hidden" name="ts" value={new Date().getTime()} />
          </form>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  query: state.session.query,
  accountDescriptors: getBrokerageAccountDescriptors(state),
  isBrokerage: isBrokerageOwner(state),
  requestConfig: state.requestStatus
});

export default withRouter(connect(mapStateToProps)(ExternalAuthorizationForm));
